import { Button, Checkbox, Modal, Popconfirm, notification } from "antd";
import SidebarLayout from "components/Layout/Layout";
import dayjs from "dayjs";
import NewRoleModalContent from "pages/Role/AddNewRoleGroup";
import AddUsersToRoleInterface from "pages/Role/AddUserToRole";
import { useState, useEffect, useMemo } from "react";
import type { NotificationArgsProps } from 'antd';
import React from "react";
import { useTheme } from "components/provider/theme";
import { API_URL } from "env";
import PermissionEdit from "./EditRole";
const Context = React.createContext({ name: 'RoleNotification' });

interface PermissionPivot {
    role_id: number;
    permission_id: number;
    created_at: string;
    updated_at: string;
};

interface Permission {
    id: number;
    name: string;
    display_name: string;
    module: string;
    action: string;
    pivot: PermissionPivot;
};

interface PermissionDisplay {
    id: number;
    name: string;
    display_name: string;
    module: string;
    action: string;
};

interface RoleItem {
    id: number;
    name: string;
    display_name: string;
    description: string;
    is_active: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
    permissions: Permission[];
    permissions_count: number;
    users_count: number;
    permissions_display: PermissionDisplay[];
};

interface RoleApiData {
    total: number;
    current_page: number;
    items: RoleItem[];
};

interface RoleApiResponse {
    status: boolean;
    data: RoleApiData;
};

export type { RoleApiResponse, RoleItem, Permission, PermissionDisplay, PermissionPivot };

const RoleMainPage = () => {
    const [roleData, setRoleData] = useState([]);
    const [permission, setPermission] = useState<Permission[]>([]);
    const [isEditUser, setIsEditUser] = useState(false);
    const [addRoleModal, setAddRoleModal] = useState(false);
    const fetchPermission = async () => {
        try {
            const response = await fetch(`${API_URL}api/v1/be/permissions`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (data.status) {
                setPermission(data.data.items);
            } else {
                console.error('Failed to fetch permissions:', data.message);
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    };
    const fetchRoleData = async () => {
        try {
            const response = await fetch(`${API_URL}api/v1/be/roles`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (data.status) {
                setRoleData(data.data.items);
            } else {
                console.error('Failed to fetch roles:', data.message);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };
    // Call the fetch function when the component mounts
    useEffect(() => {
        fetchRoleData();
        fetchPermission();
    }, []);
    return (
        <SidebarLayout
            title={
                (
                    <h4 className="text-2xl font-bold mb-4 text-gray-800 py-4">
                        Role Management
                    </h4>
                )
            }
        >
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Roles</h2>
                <Button
                    type="primary"
                    onClick={() => {
                        setAddRoleModal(true);
                    }}
                >
                    Add New Role
                </Button>
                <Modal
                    title="Add New Role"
                    open={addRoleModal}
                    onCancel={() => {
                        setAddRoleModal(false);
                    }}
                    style={{ top: 50 }}
                    width={1000}
                    footer={null}
                >
                    <NewRoleModalContent onClose={() => {
                        setAddRoleModal(false);
                        fetchRoleData();
                        fetchPermission();
                    }} />
                </Modal>
            </div>

            <div className="grid lg:!grid-cols-3 grid-cols-1 gap-4 w-full ">
                {
                    roleData.map((role: RoleItem) => (
                        <RolesDetail
                            key={role.id}
                            role={role}
                            permission={permission}
                            update={() => {
                                fetchRoleData();
                                fetchPermission();
                            }}
                        />
                    ))
                }
            </div>
        </SidebarLayout>
    );
}

const RolesDetail = ({ role, permission, update }: { role: RoleItem, permission: Permission[], update: () => void }) => {
    const [isEditUser, setIsEditUser] = useState(false);
    const [editRole, setEditRole] = useState<boolean>(false);
    const [api, contextHolder] = notification.useNotification();

    const openNotification = (message: string, title: string) => {
        api.info({
            message: title,
            description: message,
            placement: 'topRight',
        });
    };
    async function deleteRole(roleId: number) {
        try {
            const response = await fetch(`${API_URL}api/v1/be/role/${roleId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (data.status) {
                console.log('Role deleted successfully');
                update(); // Refresh the role data after deletion
            } else {
                openNotification(
                    typeof data.error === 'string'
                        ? data.error
                        : JSON.stringify(data.error),
                    'Error'
                );
            }
        } catch (error) {
            console.error('Error deleting role:', error);
        }
    }
    const contextValue = useMemo(() => ({ name: 'Ant Design' }), []);
    const { theme } = useTheme();
    return (
        <Context.Provider value={contextValue}>
            {contextHolder} 
            <div className={`rounded-lg border  p-6 transition-colors   flex flex-col justify-between gap-4 ${ theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>

                <div className="flex flex-col gap-4">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                            <div
                                className="w-12 h-12 rounded-lg flex items-center justify-center"
                                style={{
                                    backgroundColor: "rgba(59, 130, 246, 0.125)",
                                    border: "2px solid rgb(59, 130, 246)",
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="lucide lucide-users"
                                    style={{ color: "rgb(59, 130, 246)" }}
                                >
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">{role.display_name}</h3>
                                <p className="text-gray-400 text-sm">{role.display_name}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                className="p-1 transition-colors"
                                title="Chỉnh sửa"
                                onClick={() => {
                                    setEditRole(true);
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="lucide lucide-square-pen"
                                >
                                    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z" />
                                </svg>
                            </button>
                            <Modal
                                title={`Edit Role: `}
                                open={editRole}
                                onCancel={() => {
                                    setEditRole(false);
                                }}
                                style={{ top: 50 }}
                                width={1000}
                                footer={null}
                            >
                                <PermissionEdit permission={permission} role={role} 
                                    onClose={() => {
                                        update();
                                        setEditRole(false);
                                    }}
                                />

                            </Modal>

                            <Popconfirm
                                title={`Delete the Role ${role.display_name}?`}
                                description="Are you sure to delete this Role?"
                                onConfirm={() => {
                                    deleteRole(role.id);
                                }}
                                okText="Yes"
                                cancelText="No"
                            >
                                <button
                                    onClick={
                                        () => {

                                        }
                                    }
                                    className={`p-1 cursor-pointer transition-colors ${ theme === 'dark' ? 'text-white hover:text-red-500' : 'text-gray-800 hover:text-red-500'}`}
                                    title="Xóa"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="lucide lucide-trash2"
                                    >
                                        <path d="M3 6h18" />
                                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                        <line x1="10" x2="10" y1="11" y2="17" />
                                        <line x1="14" x2="14" y1="11" y2="17" />
                                    </svg>
                                </button>
                            </Popconfirm>

                        </div>
                    </div>

                    <div className="space-y-3 ">
                        <div className="flex items-center text-sm">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-calendar mr-2"
                            >
                                <path d="M8 2v4" />
                                <path d="M16 2v4" />
                                <rect width="18" height="18" x="3" y="4" rx="2" />
                                <path d="M3 10h18" />
                            </svg>
                            <span>Chỉnh sửa lần cuối {dayjs(role.updated_at).format('YYYY-MM-DD HH:mm:ss')}</span>
                        </div>

                        <div className="flex items-center  text-sm">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-user mr-2"
                            >
                                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                            </svg>
                            <span>Có {role.users_count} tài khoản đang thuộc nhóm</span>
                        </div>

                        <div className="mt-4 w-full">
                            <p className=" text-sm font-medium mb-2">Quyền hạn:</p>
                            <div className="flex flex-wrap gap-1 w-full">
                                {role.permissions_display?.slice(0, 7).map((perm: PermissionDisplay) => (
                                    <span
                                        key={perm.id}
                                        className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded"
                                    >
                                        {perm.display_name}
                                    </span>
                                ))}
                                {role.permissions_display?.length > 8 && (
                                    <span className="bg-gray-200 text-gray-700 text-xs font-medium px-2.5 py-0.5 rounded">
                                        +{role.permissions_display?.length - 7} khác
                                    </span>
                                )}
                            </div>
                        </div>
                        <Modal
                            title={`Edit Role: `}
                            open={isEditUser} // This should be controlled by a state variable
                            onCancel={() => {
                                setIsEditUser(false);
                            }}
                            style={{ top: 50 }}
                            width={1000}
                            footer={null}
                        >
                            <AddUsersToRoleInterface role={role} onClose={() => {
                                update();
                                setIsEditUser(false);
                            }} />
                        </Modal>

                    </div>

                </div>
                <div className={`pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'} flex flex-col lg:flex-row lg:items-center lg:justify-between`}>
                    <button
                        onClick={() => {
                            setIsEditUser(true);
                        }}
                        type="button"
                        className={`w-full cursor-pointer px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${theme === 'dark' ? 'bg-gray-700 !text-white hover:bg-gray-600' : 'bg-blue-500 !text-white hover:bg-blue-600'}`}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-user-plus mr-2"
                        >
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <line x1="19" x2="19" y1="8" y2="14" />
                            <line x1="22" x2="16" y1="11" y2="11" />
                        </svg>
                        Quản lý thành viên
                    </button>
                </div>
            </div>
        </Context.Provider>
    )
}
export default RoleMainPage;